import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Save } from 'lucide-react';
import { baseUrlsService, getApiErrorMessage } from '../../../../components/api/baseUrlsService';

const inputClass =
  'w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed';

const BaseUrlsCard: React.FC = () => {
  const [omniChatAssistantUrl, setOmniChatAssistantUrl] = useState('');
  const [llmGatewayUrl, setLlmGatewayUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const saved = await baseUrlsService.get();
      setOmniChatAssistantUrl(saved.omniChatAssistantUrl);
      setLlmGatewayUrl(saved.llmGatewayUrl);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load base URLs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await baseUrlsService.upsert(omniChatAssistantUrl, llmGatewayUrl);
      setOmniChatAssistantUrl(saved.omniChatAssistantUrl);
      setLlmGatewayUrl(saved.llmGatewayUrl);
      setSuccess('Base URLs saved.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save base URLs.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
      <div className="flex items-center gap-2 mb-4 text-gray-400">
        <Link2 className="w-4 h-4" />
        <h3 className="font-medium text-white text-base">Base URLs</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Public origins used by assistants and LLM inference. Empty Omni Chat Assistant falls back to the portal origin.
        Empty LLM gateway falls back to the platform gateway origin.
      </p>
      <label className="flex flex-col gap-1.5 mb-4">
        <span className="text-gray-400 text-xs">Omni Chat Assistant</span>
        <input
          type="url"
          value={omniChatAssistantUrl}
          onChange={(event) => setOmniChatAssistantUrl(event.target.value)}
          placeholder="https://assistant.example.com"
          className={inputClass}
          aria-label="Omni Chat Assistant URL"
          disabled={loading || saving}
          autoComplete="off"
        />
      </label>
      <label className="flex flex-col gap-1.5 mb-4">
        <span className="text-gray-400 text-xs">LLM gateway</span>
        <input
          type="url"
          value={llmGatewayUrl}
          onChange={(event) => setLlmGatewayUrl(event.target.value)}
          placeholder="https://gateway.selfic.ai"
          className={inputClass}
          aria-label="LLM gateway URL"
          disabled={loading || saving}
          autoComplete="off"
        />
      </label>
      {error ? <p className="text-xs text-rose-400 mb-3">{error}</p> : null}
      {success ? <p className="text-xs text-emerald-400 mb-3">{success}</p> : null}
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={loading || saving}
        className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm transition-colors"
      >
        <Save className="w-4 h-4" />
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

export default BaseUrlsCard;
